import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Text from '../../components/common/Text';
import SearchBar from '../../components/shared/SearchBar';
import { useTheme } from '../../hooks/useTheme';
import ResponsiveContainer from '../../components/shared/ResponsiveContainer';
import DashboardLayout from '../../components/layout/DashboardLayout';
import TopNavigationBar from '../../components/shared/TopNavigationBar';
import UsersGrid from '../../components/custom/admin/UsersGrid';
import MoodboardsGrid from '../../components/custom/admin/MoodboardsGrid';
import SubscriptionsGrid from '../../components/custom/admin/SubscriptionsGrid';
import FeedbackGrid from '../../components/custom/admin/FeedbackGrid';
import AuditLogsGrid from '../../components/custom/admin/AuditLogsGrid';
import InvitesGrid from '../../components/custom/admin/InvitesGrid';
import FeatureFlagsGrid from '../../components/custom/admin/FeatureFlagsGrid';
import UserDetailModal from '../../components/custom/admin/UserDetailModal';
import AnalyticsView from '../../components/custom/admin/AnalyticsView';
import SupportView from '../../components/custom/admin/SupportView';
import PermissionsView from '../../components/custom/admin/PermissionsView';
import UserAvatar from '../../components/shared/UserAvatar';
import { useAuth } from '../../hooks/useAuth';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../types/navigation';
import { palette } from '../../utils/palette';
import {
  useAdminStats,
  useUsers,
  useSubscriptions,
  useMoodboards,
  useFeedback,
  useUpdateFeedbackStatus,
  useSuspendUser,
  useActivateUser,
  useDeleteUser,
  useUpdateUserRole,
  useUpdateUserSubscription,
  useHideMoodboard,
  useShowMoodboard,
  useRejectMoodboard,
  useDeleteMoodboard,
  useFlagMoodboard,
  useFlaggedMoodboards,
  useRejectFlaggedMoodboard,
  useApproveFlaggedMoodboard,
  useDeleteFlaggedMoodboard,
  useAuditLogs,
  useUserDetails,
  useInvites,
  useFeatureFlags,
} from '../../hooks/useAdminData';

type AdminScreenNavigationProp = NativeStackNavigationProp<RootStackParamList>;
type AdminScreenRouteProp = RouteProp<RootStackParamList, 'Admin'>;

type AdminView =
  | 'dashboard'
  | 'users'
  | 'subscriptions'
  | 'moodboards'
  | 'feedback'
  | 'flagged'
  | 'audit-logs'
  | 'analytics'
  | 'support'
  | 'permissions'
  | 'profile'
  | 'invites'
  | 'feature-flags';

// Helper functions for feedback status
const getStatusColor = (
  status: string
): { background: string; color: string } => {
  switch (status) {
    case 'new':
      return {
        background: `${palette.brightMatteRed}20`,
        color: palette.brightMatteRed,
      };
    case 'reviewing':
      return {
        background: `${palette.brightBlue}20`,
        color: palette.brightBlue,
      };
    case 'in_progress':
      return {
        background: `${palette.lightOrange}20`,
        color: palette.lightOrange,
      };
    case 'done':
      return {
        background: `${palette.cyanLimeGreen}20`,
        color: palette.cyanLimeGreen,
      };
    case 'no_action_needed':
      return {
        background: `${palette.darkGrayBlue}20`,
        color: palette.darkGrayBlue,
      };
    default:
      return {
        background: `${palette.darkGrayBlue}20`,
        color: palette.darkGrayBlue,
      };
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'new':
      return 'New';
    case 'reviewing':
      return 'Reviewing';
    case 'in_progress':
      return 'In Progress';
    case 'done':
      return 'Done';
    case 'no_action_needed':
      return 'No Action';
    default:
      return 'Unknown';
  }
};

const AdminScreen = () => {
  const { colors } = useTheme();
  const { user, refreshUser, isLoading: authLoading, signOut } = useAuth();
  const navigation = useNavigation<AdminScreenNavigationProp>();
  const route = useRoute<AdminScreenRouteProp>();
  const routeHash = route.params?.hash;
  const routeSection = route.params?.section || 'dashboard';
  const [activeView, setActiveView] = useState<AdminView>(
    routeSection as AdminView
  );

  // Pagination states
  const [usersPagination, setUsersPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });
  const [subscriptionsPagination, setSubscriptionsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });
  const [moodboardsPagination, setMoodboardsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });
  const [feedbackPagination, setFeedbackPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });
  const [auditLogsPagination, setAuditLogsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });
  const [invitesPagination, setInvitesPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 1,
  });
  const [featureFlagsPagination, setFeatureFlagsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });

  // Search states
  const [usersSearch, setUsersSearch] = useState('');
  const [subscriptionsSearch, setSubscriptionsSearch] = useState('');
  const [moodboardsSearch, setMoodboardsSearch] = useState('');
  const [feedbackSearch, setFeedbackSearch] = useState('');
  const [auditLogsSearch, setAuditLogsSearch] = useState('');
  const [flaggedSearch, setFlaggedSearch] = useState('');
  const [invitesSearch, setInvitesSearch] = useState('');
  const [featureFlagsSearch, setFeatureFlagsSearch] = useState('');

  // Moodboard filter states
  const [moodboardStatusFilter, setMoodboardStatusFilter] =
    useState<string>('');
  const [moodboardUserFilter, setMoodboardUserFilter] = useState<string>('');
  const [moodboardSortBy, setMoodboardSortBy] = useState<string>('created_at');
  const [moodboardSortOrder, setMoodboardSortOrder] = useState<string>('desc');
  const [moodboardDateFrom, setMoodboardDateFrom] = useState<string>('');
  const [moodboardDateTo, setMoodboardDateTo] = useState<string>('');

  // Grid refs for search
  const usersGridRef = useRef<{ getApi?: () => unknown } | null>(null);
  const moodboardsGridRef = useRef<{ getApi?: () => unknown } | null>(null);
  const subscriptionsGridRef = useRef<{ getApi?: () => unknown } | null>(null);
  const invitesGridRef = useRef<{ getApi?: () => unknown } | null>(null);
  const feedbackGridRef = useRef<{ getApi?: () => unknown } | null>(null);
  const auditLogsGridRef = useRef<{ getApi?: () => unknown } | null>(null);

  const [redirecting, setRedirecting] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [userDetailModalVisible, setUserDetailModalVisible] = useState(false);

  // TanStack Query hooks - only fetch data for the active view
  const { data: stats, isLoading: statsLoading } = useAdminStats();
  const { data: usersData, isLoading: usersLoading } = useUsers(
    usersPagination,
    {
      enabled: activeView === 'users' && !!user?.is_admin,
    }
  );
  const { data: subscriptionsData, isLoading: subscriptionsLoading } =
    useSubscriptions(subscriptionsPagination, {
      enabled: activeView === 'subscriptions' && !!user?.is_admin,
    });
  const { data: moodboardsData, isLoading: moodboardsLoading } = useMoodboards(
    moodboardsPagination,
    {
      moderation_status: moodboardStatusFilter || undefined,
      user_id: moodboardUserFilter ? parseInt(moodboardUserFilter) : undefined,
      sort_by: moodboardSortBy,
      sort_order: moodboardSortOrder,
      date_from: moodboardDateFrom || undefined,
      date_to: moodboardDateTo || undefined,
    },
    {
      enabled: activeView === 'moodboards' && !!user?.is_admin,
    }
  );

  const [flaggedPagination, setFlaggedPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });
  const { data: flaggedData } = useFlaggedMoodboards(flaggedPagination, {
    enabled: activeView === 'flagged' && !!user?.is_admin,
  });
  const { data: feedbackData, isLoading: feedbackLoading } = useFeedback(
    feedbackPagination,
    {
      enabled: activeView === 'feedback' && !!user?.is_admin,
    }
  );
  const updateFeedbackStatus = useUpdateFeedbackStatus();
  const { data: auditLogsData, isLoading: auditLogsLoading } = useAuditLogs(
    auditLogsPagination,
    {},
    {
      enabled: activeView === 'audit-logs' && !!user?.is_admin,
    }
  );
  const { data: invitesData, isLoading: invitesLoading } = useInvites(
    invitesPagination,
    {},
    {
      enabled: activeView === 'invites' && !!user?.is_admin,
    }
  );
  const { data: adminUserDetails, isLoading: adminUserDetailsLoading } =
    useUserDetails(
      activeView === 'profile' && user?.id
        ? typeof user.id === 'number'
          ? user.id
          : parseInt(String(user.id))
        : null
    );

  // Mutations
  const suspendUserMutation = useSuspendUser();
  const activateUserMutation = useActivateUser();
  const deleteUserMutation = useDeleteUser();
  const updateRoleMutation = useUpdateUserRole();
  const updateSubscriptionMutation = useUpdateUserSubscription();
  const hideMoodboardMutation = useHideMoodboard();
  const showMoodboardMutation = useShowMoodboard();
  const rejectMoodboardMutation = useRejectMoodboard();
  const deleteMoodboardMutation = useDeleteMoodboard();
  const flagMoodboardMutation = useFlagMoodboard();
  const rejectFlaggedMutation = useRejectFlaggedMoodboard();
  const approveFlaggedMutation = useApproveFlaggedMoodboard();
  const deleteFlaggedMutation = useDeleteFlaggedMoodboard();

  // Extract data from query results
  const users = usersData?.items || [];
  const subscriptions = subscriptionsData?.items || [];
  const moodboards = moodboardsData?.items || [];
  const flaggedMoodboards = React.useMemo(
    () => flaggedData?.items || [],
    [flaggedData?.items]
  );
  const feedback = feedbackData?.items || [];
  const auditLogs = React.useMemo(
    () => auditLogsData?.items || [],
    [auditLogsData?.items]
  );

  // Filter flagged moodboards for mobile view
  const filteredFlaggedMoodboards = useMemo(() => {
    if (!flaggedSearch.trim()) return flaggedMoodboards;
    const lowerSearch = flaggedSearch.toLowerCase();
    return flaggedMoodboards.filter((mb: Record<string, unknown>) => {
      const searchFields = [
        mb.name,
        mb.user_id?.toString(),
        mb.user_email,
        mb.user_name,
        mb.id?.toString(),
      ];
      return searchFields.some(
        (field) => field && String(field).toLowerCase().includes(lowerSearch)
      );
    });
  }, [flaggedMoodboards, flaggedSearch]);

  // Filter audit logs for mobile view
  const filteredAuditLogs = React.useMemo(() => {
    if (!auditLogsSearch.trim()) return auditLogs;
    const lowerSearch = auditLogsSearch.toLowerCase();
    return auditLogs.filter((log: Record<string, unknown>) => {
      const searchFields = [
        log.action,
        log.action_type,
        log.target_type,
        log.target_name,
        log.admin_user_email,
        log.reason,
        log.id?.toString(),
        log.target_id?.toString(),
      ];
      return searchFields.some(
        (field) => field && String(field).toLowerCase().includes(lowerSearch)
      );
    });
  }, [auditLogs, auditLogsSearch]);

  // Filter invites for mobile view
  const invites = React.useMemo(
    () => invitesData?.items || [],
    [invitesData?.items]
  );
  const filteredInvites = React.useMemo(() => {
    if (!invitesSearch.trim()) return invites;
    const lowerSearch = invitesSearch.toLowerCase();
    return invites.filter((invite: Record<string, unknown>) => {
      const searchFields = [
        invite.code,
        invite.inviter_name,
        invite.inviter_email,
        invite.invitee_name,
        invite.invitee_email,
        invite.id?.toString(),
        invite.inviter_id?.toString(),
        invite.invitee_id?.toString(),
      ];
      return searchFields.some(
        (field) => field && String(field).toLowerCase().includes(lowerSearch)
      );
    });
  }, [invites, invitesSearch]);

  // Update pagination when data changes
  useEffect(() => {
    if (usersData) {
      setUsersPagination((prev) => ({
        ...prev,
        total: usersData.total,
        pages: usersData.pages,
      }));
    }
  }, [usersData]);

  useEffect(() => {
    if (subscriptionsData) {
      setSubscriptionsPagination((prev) => ({
        ...prev,
        total: subscriptionsData.total,
        pages: subscriptionsData.pages,
      }));
    }
  }, [subscriptionsData]);

  useEffect(() => {
    if (moodboardsData) {
      setMoodboardsPagination((prev) => ({
        ...prev,
        total: moodboardsData.total,
        pages: moodboardsData.pages,
      }));
    }
  }, [moodboardsData]);

  useEffect(() => {
    if (flaggedData) {
      setFlaggedPagination((prev) => ({
        ...prev,
        total: flaggedData.total,
        pages: flaggedData.pages,
      }));
    }
  }, [flaggedData]);

  useEffect(() => {
    if (feedbackData) {
      setFeedbackPagination((prev) => ({
        ...prev,
        total: feedbackData.total,
        pages: feedbackData.pages,
      }));
    }
  }, [feedbackData]);

  useEffect(() => {
    if (auditLogsData) {
      setAuditLogsPagination((prev) => ({
        ...prev,
        total: auditLogsData.total,
        pages: auditLogsData.pages,
      }));
    }
  }, [auditLogsData]);

  useEffect(() => {
    if (invitesData) {
      setInvitesPagination((prev) => ({
        ...prev,
        total: invitesData.total,
        pages: Math.ceil(invitesData.total / prev.limit),
      }));
    }
  }, [invitesData]);

  const { data: featureFlagsData, isLoading: featureFlagsLoading } =
    useFeatureFlags({
      page: featureFlagsPagination.page,
      limit: featureFlagsPagination.limit,
    });

  useEffect(() => {
    if (featureFlagsData) {
      setFeatureFlagsPagination((prev) => ({
        ...prev,
        total: featureFlagsData.total,
        pages: featureFlagsData.pages,
      }));
    }
  }, [featureFlagsData]);

  const loading =
    statsLoading ||
    usersLoading ||
    subscriptionsLoading ||
    moodboardsLoading ||
    feedbackLoading ||
    auditLogsLoading ||
    invitesLoading ||
    featureFlagsLoading;

  // Validate admin route hash
  useEffect(() => {
    const validateHash = async () => {
      try {
        const { API_URL } = await import('../../constants/config');
        const response = await fetch(`${API_URL}/admin/validate-hash`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ hash: routeHash }),
        });

        if (!response.ok) {
          await response.json().catch(() => ({ detail: 'Invalid hash' }));
          Alert.alert(
            'Access Denied',
            'Invalid admin route hash. Please use the correct admin portal URL.',
            [
              {
                text: 'OK',
                onPress: () => {
                  // Redirect to landing page
                  if (Platform.OS === 'web') {
                    window.location.href = '/';
                  } else {
                    navigation.replace('Landing');
                  }
                },
              },
            ]
          );
          return;
        }
      } catch {
        Alert.alert(
          'Error',
          'Failed to validate admin route. Please try again.',
          [
            {
              text: 'OK',
              onPress: () => {
                if (Platform.OS === 'web') {
                  window.location.href = '/';
                } else {
                  navigation.replace('Landing');
                }
              },
            },
          ]
        );
      }
    };

    if (routeHash) {
      validateHash();
    } else {
      // No hash provided, deny access
      Alert.alert(
        'Access Denied',
        'Admin route hash is required. Please use the correct admin portal URL.',
        [
          {
            text: 'OK',
            onPress: () => {
              if (Platform.OS === 'web') {
                window.location.href = '/';
              } else {
                navigation.replace('Landing');
              }
            },
          },
        ]
      );
    }
  }, [routeHash, navigation]);

  // Check authentication and admin status immediately
  useEffect(() => {
    if (authLoading) {
      return;
    }

    // Don't proceed if hash validation failed
    if (!routeHash) {
      return;
    }

    if (!user) {
      if (!redirecting) {
        setRedirecting(true);
        setTimeout(() => {
          navigation.replace('AdminLogin', { hash: routeHash });
        }, 100);
      }
      return;
    }

    if (!user.is_admin) {
      if (!redirecting) {
        setRedirecting(true);
        setTimeout(() => {
          Alert.alert(
            'Access Denied',
            'You must be an admin to access this page.',
            [
              {
                text: 'OK',
                onPress: () => {
                  navigation.replace('AdminLogin', { hash: routeHash });
                },
              },
            ]
          );
        }, 100);
      }
      return;
    }

    // Verify admin status with backend to ensure token is valid
    const verifyAdminStatus = async () => {
      try {
        const { apiFetchWithTokenRefresh } =
          await import('../../utils/tokenInterceptor');
        const { API_URL } = await import('../../constants/config');
        const response = await apiFetchWithTokenRefresh(`${API_URL}/auth/me`, {
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          const userData = await response.json();
          if (!userData.is_admin) {
            Alert.alert(
              'Admin Access Required',
              'Your account does not have admin privileges. Please contact an administrator or log out and log back in if your admin status was recently granted.',
              [
                {
                  text: 'Log Out',
                  onPress: () =>
                    signOut().then(() =>
                      navigation.replace('AdminLogin', { hash: routeHash })
                    ),
                },
                { text: 'Cancel', style: 'cancel' },
              ]
            );
          }
        }
      } catch {
        // Verify failed
      }
    };

    verifyAdminStatus();
    setRedirecting(false);
    // Data will be loaded automatically by TanStack Query hooks
  }, [user, authLoading, navigation, signOut, redirecting, routeHash]);

  // Sync activeView with route params
  useEffect(() => {
    const section = routeSection || 'dashboard';
    if (section !== activeView) {
      setActiveView(section as AdminView);
    }
  }, [routeSection, activeView]);

  // Reset pagination to page 1 when switching views
  useEffect(() => {
    if (user?.is_admin && !authLoading) {
      if (activeView === 'users') {
        setUsersPagination((prev) => ({ ...prev, page: 1 }));
      } else if (activeView === 'subscriptions') {
        setSubscriptionsPagination((prev) => ({ ...prev, page: 1 }));
      } else if (activeView === 'moodboards') {
        setMoodboardsPagination((prev) => ({ ...prev, page: 1 }));
      } else if (activeView === 'flagged') {
        setFlaggedPagination((prev) => ({ ...prev, page: 1 }));
      } else if (activeView === 'feedback') {
        setFeedbackPagination((prev) => ({ ...prev, page: 1 }));
      } else if (activeView === 'audit-logs') {
        setAuditLogsPagination((prev) => ({ ...prev, page: 1 }));
      } else if (activeView === 'invites') {
        setInvitesPagination((prev) => ({ ...prev, page: 1 }));
      }
    }
  }, [activeView, user?.is_admin, authLoading]);

  // TanStack Query automatically refetches when pagination changes

  // Data loading is now handled by TanStack Query hooks

  const handleViewUserDetails = (userId: number) => {
    setSelectedUserId(userId);
    setUserDetailModalVisible(true);
  };

  const handleSuspendUser = (userId: number) => {
    suspendUserMutation.mutate(userId);
  };

  const handleDeleteUser = (userId: number) => {
    Alert.alert(
      'Confirm Delete',
      'Are you sure you want to delete this user? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => deleteUserMutation.mutate(userId),
        },
      ]
    );
  };

  const handleUpdateRole = (userId: number, role: string) => {
    updateRoleMutation.mutate({ userId, role });
  };

  const handleBanUser = (userId: number) => {
    // Ban is same as suspend
    suspendUserMutation.mutate(userId);
  };

  const handleActivateUser = (userId: number) => {
    activateUserMutation.mutate(userId);
  };

  const handleHideMoodboard = (moodboardId: number) => {
    hideMoodboardMutation.mutate(moodboardId);
  };

  const handleShowMoodboard = (moodboardId: number) => {
    showMoodboardMutation.mutate(moodboardId);
  };

  const handleRejectMoodboard = (moodboardId: number) => {
    rejectMoodboardMutation.mutate(moodboardId);
  };

  const handleDeleteMoodboard = (moodboardId: number) => {
    Alert.alert(
      'Confirm Delete',
      'Are you sure you want to permanently delete this moodboard? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => deleteMoodboardMutation.mutate(moodboardId),
        },
      ]
    );
  };

  const handleFlagMoodboard = (moodboardId: number) => {
    flagMoodboardMutation.mutate(moodboardId);
  };

  // Flagged content handlers with confirmation and reason
  const handleRejectFlaggedMoodboard = (
    moodboardId: number,
    moodboardName?: string
  ) => {
    if (Platform.OS === 'web') {
      const reason = window.prompt(
        `Reject Flagged Request\n\nPlease provide a reason for rejecting the flagged request${moodboardName ? ` for "${moodboardName}"` : ''}.\n\nThis will notify the reporter that no violation was found.`
      );
      if (reason === null) {
        return; // User cancelled
      }
      if (!reason || !reason.trim()) {
        Alert.alert('Error', 'Reason is required to reject a flagged request.');
        return;
      }
      rejectFlaggedMutation.mutate({ moodboardId, reason: reason.trim() });
    } else {
      Alert.prompt(
        'Reject Flagged Request',
        `Please provide a reason for rejecting the flagged request${moodboardName ? ` for "${moodboardName}"` : ''}.\n\nThis will notify the reporter that no violation was found.`,
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Reject',
            style: 'destructive',
            onPress: (reason) => {
              if (!reason || !reason.trim()) {
                Alert.alert(
                  'Error',
                  'Reason is required to reject a flagged request.'
                );
                return;
              }
              rejectFlaggedMutation.mutate({ moodboardId, reason });
            },
          },
        ],
        'plain-text'
      );
    }
  };

  const handleApproveFlaggedMoodboard = (
    moodboardId: number,
    moodboardName?: string
  ) => {
    if (Platform.OS === 'web') {
      const reason = window.prompt(
        `Approve Flagged Content\n\nPlease provide a reason for approving the flagged content${moodboardName ? ` "${moodboardName}"` : ''}.\n\nThis will hide the content and notify the reporter that a violation was found.`
      );
      if (reason === null) {
        return; // User cancelled
      }
      if (!reason || !reason.trim()) {
        Alert.alert(
          'Error',
          'Reason is required to approve a flagged request.'
        );
        return;
      }
      approveFlaggedMutation.mutate({ moodboardId, reason: reason.trim() });
    } else {
      Alert.prompt(
        'Approve Flagged Content',
        `Please provide a reason for approving the flagged content${moodboardName ? ` "${moodboardName}"` : ''}.\n\nThis will hide the content and notify the reporter that a violation was found.`,
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Approve',
            style: 'default',
            onPress: (reason) => {
              if (!reason || !reason.trim()) {
                Alert.alert(
                  'Error',
                  'Reason is required to approve a flagged request.'
                );
                return;
              }
              approveFlaggedMutation.mutate({ moodboardId, reason });
            },
          },
        ],
        'plain-text'
      );
    }
  };

  const handleDeleteFlaggedMoodboard = (
    moodboardId: number,
    moodboardName?: string
  ) => {
    if (Platform.OS === 'web') {
      const confirmed = window.confirm(
        `Delete Flagged Content\n\nAre you sure you want to permanently delete${moodboardName ? ` "${moodboardName}"` : ' this moodboard'}?\n\nWARNING: This action cannot be undone.`
      );
      if (!confirmed) {
        return;
      }
      const reason = window.prompt(
        'Please provide a reason for deleting this moodboard (optional):'
      );
      deleteFlaggedMutation.mutate({ moodboardId, reason: reason?.trim() });
    } else {
      Alert.prompt(
        'Delete Flagged Content',
        `Please provide a reason for permanently deleting${moodboardName ? ` "${moodboardName}"` : ' this moodboard'}.\n\nWARNING: This action cannot be undone.`,
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: (reason) => {
              deleteFlaggedMutation.mutate({
                moodboardId,
                reason: reason?.trim(),
              });
            },
          },
        ],
        'plain-text'
      );
    }
  };

  const renderPagination = (
    pagination: { page: number; pages: number; total: number },
    setPaginationFunc: (p: {
      page: number;
      pages: number;
      total: number;
    }) => void
  ) => {
    if (pagination.pages <= 1) return null;

    return (
      <View style={styles.pagination}>
        <TouchableOpacity
          style={[
            styles.paginationButton,
            { backgroundColor: colors.card, borderColor: colors.border },
          ]}
          onPress={() => {
            if (pagination.page > 1) {
              setPaginationFunc({ ...pagination, page: pagination.page - 1 });
            }
          }}
          disabled={pagination.page === 1}
        >
          <Ionicons
            name="chevron-back"
            size={20}
            color={pagination.page === 1 ? colors.secondary : colors.text}
          />
        </TouchableOpacity>

        <Text style={[styles.paginationText, { color: colors.text }]}>
          Page {pagination.page} of {pagination.pages} ({pagination.total}{' '}
          total)
        </Text>

        <TouchableOpacity
          style={[
            styles.paginationButton,
            { backgroundColor: colors.card, borderColor: colors.border },
          ]}
          onPress={() => {
            if (pagination.page < pagination.pages) {
              setPaginationFunc({ ...pagination, page: pagination.page + 1 });
            }
          }}
          disabled={pagination.page === pagination.pages}
        >
          <Ionicons
            name="chevron-forward"
            size={20}
            color={
              pagination.page === pagination.pages
                ? colors.secondary
                : colors.text
            }
          />
        </TouchableOpacity>
      </View>
    );
  };

  const filterData = (
    data: Record<string, unknown>[],
    searchValue: string,
    searchFields: string[]
  ) => {
    if (!searchValue.trim()) return data;
    const lowerSearch = searchValue.toLowerCase();
    return data.filter((item) =>
      searchFields.some((field) => {
        const value = item[field];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(lowerSearch);
      })
    );
  };

  // Filtered data for mobile views
  const filteredUsers = filterData(users, usersSearch, [
    'email',
    'name',
    'subscription_plan',
    'subscription_status',
  ]);
  const filteredSubscriptions = filterData(subscriptions, subscriptionsSearch, [
    'email',
    'subscription_plan',
    'subscription_status',
  ]);
  const filteredMoodboards = filterData(moodboards, moodboardsSearch, [
    'name',
    'user_id',
  ]);
  const filteredFeedback = filterData(feedback, feedbackSearch, [
    'subject',
    'message',
    'email',
    'status',
    'category',
  ]);

  // Show loading while auth is loading
  if (authLoading) {
    return (
      <ResponsiveContainer>
        <View
          style={[
            styles.container,
            styles.loadingContainer,
            { backgroundColor: colors.background },
          ]}
        >
          <Text style={[styles.loading, { color: colors.secondary }]}>
            Loading...
          </Text>
        </View>
      </ResponsiveContainer>
    );
  }

  // Show redirecting message if not authenticated
  if (!user) {
    return (
      <ResponsiveContainer>
        <View
          style={[
            styles.container,
            styles.loadingContainer,
            { backgroundColor: colors.background },
          ]}
        >
          <Text style={[styles.loading, { color: colors.secondary }]}>
            Redirecting to login...
          </Text>
        </View>
      </ResponsiveContainer>
    );
  }

  // Show access denied if not admin
  if (!user.is_admin) {
    return (
      <ResponsiveContainer>
        <View
          style={[
            styles.container,
            styles.loadingContainer,
            { backgroundColor: colors.background },
          ]}
        >
          <Text
            style={[
              styles.loading,
              { color: colors.errorText || palette.brightMatteRed },
            ]}
          >
            Access Denied. Redirecting...
          </Text>
        </View>
      </ResponsiveContainer>
    );
  }

  const menuItems: { key: AdminView; label: string; icon: string }[] = [
    { key: 'dashboard', label: 'Dashboard', icon: 'home' },
    { key: 'profile', label: 'My Profile', icon: 'person' },
    { key: 'users', label: 'Users', icon: 'people' },
    { key: 'subscriptions', label: 'Subscriptions', icon: 'card' },
    { key: 'moodboards', label: 'Moodboards', icon: 'grid' },
    { key: 'flagged', label: 'Flagged Content', icon: 'flag' },
    { key: 'permissions', label: 'Permissions', icon: 'lock-closed' },
    { key: 'feature-flags', label: 'Feature Flags', icon: 'flag-outline' },
    { key: 'feedback', label: 'Feedback', icon: 'chatbubbles' },
    { key: 'audit-logs', label: 'Audit Log', icon: 'document-text' },
    { key: 'invites', label: 'Invites', icon: 'person-add' },
    { key: 'analytics', label: 'Analytics', icon: 'stats-chart' },
    { key: 'support', label: 'Support', icon: 'help-circle' },
  ];

  const getPageTitle = () => {
    if (activeView === 'dashboard') {
      return 'Admin Dashboard';
    }
    const menuItem = menuItems.find((item) => item.key === activeView);
    return menuItem ? menuItem.label : 'Admin';
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigation.replace('AdminLogin', { hash: routeHash });
    } catch {
      Alert.alert('Error', 'Failed to logout. Please try again.');
    }
  };

  const handleTopNavLogoPress = () => {
    navigation.navigate('Landing');
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for TopNavigationBar logout
  const _handleTopNavLogoutPress = () => {
    handleLogout();
  };

  return (
    <View style={styles.container}>
      <TopNavigationBar
        onLogoPress={handleTopNavLogoPress}
        hideActions={true}
      />
      <View style={styles.contentContainer}>
        <DashboardLayout
          title="Admin Panel"
          menuItems={menuItems}
          activeView={activeView}
          onViewChange={(view) => {
            setActiveView(view as AdminView);
            // Update route params to sync with URL
            const section = view === 'dashboard' ? undefined : view;
            navigation.setParams({
              hash: routeHash,
              section: section as string | undefined,
            });
          }}
          loading={loading}
          onLogout={handleLogout}
          pageTitle={getPageTitle()}
        >
          {activeView === 'dashboard' && stats && (
            <View style={styles.section}>
              {/* Stats Grid */}
              <View style={styles.statsGrid}>
                {/* Users Stats */}
                <View
                  style={[
                    styles.statCard,
                    {
                      backgroundColor: colors.card,
                      borderColor: colors.border,
                    },
                  ]}
                >
                  <Ionicons name="people" size={32} color={colors.primary} />
                  <Text style={[styles.statValue, { color: colors.text }]}>
                    {stats.users?.total || 0}
                  </Text>
                  <Text style={[styles.statLabel, { color: colors.secondary }]}>
                    Total Users
                  </Text>
                  <View style={styles.statRow}>
                    <Text
                      style={[styles.statSubtext, { color: colors.secondary }]}
                    >
                      Active: {stats.users?.active || 0}
                    </Text>
                    <Text
                      style={[styles.statSubtext, { color: colors.secondary }]}
                    >
                      Recent: {stats.users?.recent || 0}
                    </Text>
                  </View>
                </View>

                {/* Subscriptions Stats */}
                <View
                  style={[
                    styles.statCard,
                    {
                      backgroundColor: colors.card,
                      borderColor: colors.border,
                    },
                  ]}
                >
                  <Ionicons name="card" size={32} color={colors.primary} />
                  <Text style={[styles.statValue, { color: colors.text }]}>
                    {stats.subscriptions?.pro || 0}
                  </Text>
                  <Text style={[styles.statLabel, { color: colors.secondary }]}>
                    Pro Users
                  </Text>
                  <View style={styles.statRow}>
                    <Text
                      style={[styles.statSubtext, { color: colors.secondary }]}
                    >
                      Free: {stats.subscriptions?.free || 0}
                    </Text>
                    <Text
                      style={[styles.statSubtext, { color: colors.secondary }]}
                    >
                      Trial: {stats.subscriptions?.trial || 0}
                    </Text>
                  </View>
                </View>

                {/* Moodboards Stats */}
                <View
                  style={[
                    styles.statCard,
                    {
                      backgroundColor: colors.card,
                      borderColor: colors.border,
                    },
                  ]}
                >
                  <Ionicons name="grid" size={32} color={colors.primary} />
                  <Text style={[styles.statValue, { color: colors.text }]}>
                    {stats.moodboards?.total || 0}
                  </Text>
                  <Text style={[styles.statLabel, { color: colors.secondary }]}>
                    Moodboards
                  </Text>
                  <View style={styles.statRow}>
                    <Text
                      style={[styles.statSubtext, { color: colors.secondary }]}
                    >
                      Likes: {stats.moodboards?.total_likes || 0}
                    </Text>
                    <Text
                      style={[styles.statSubtext, { color: colors.secondary }]}
                    >
                      Views: {stats.moodboards?.total_views || 0}
                    </Text>
                  </View>
                </View>

                {/* Feedback Stats */}
                <View
                  style={[
                    styles.statCard,
                    {
                      backgroundColor: colors.card,
                      borderColor: colors.border,
                    },
                  ]}
                >
                  <Ionicons
                    name="chatbubbles"
                    size={32}
                    color={colors.primary}
                  />
                  <Text style={[styles.statValue, { color: colors.text }]}>
                    {stats.feedback?.total || 0}
                  </Text>
                  <Text style={[styles.statLabel, { color: colors.secondary }]}>
                    Feedback
                  </Text>
                  <View style={styles.statRow}>
                    <Text
                      style={[styles.statSubtext, { color: colors.errorText }]}
                    >
                      New: {stats.feedback?.new || 0}
                    </Text>
                    <Text
                      style={[styles.statSubtext, { color: colors.secondary }]}
                    >
                      Resolved: {stats.feedback?.resolved || 0}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          )}

          {activeView === 'users' && (
            <View style={styles.section}>
              <SearchBar
                value={usersSearch}
                onChangeText={setUsersSearch}
                placeholder="Search users by email, name..."
                containerStyle={styles.searchBarContainer}
              />
              {Platform.OS === 'web' ? (
                <>
                  <UsersGrid
                    ref={usersGridRef}
                    users={users}
                    searchValue={usersSearch}
                    onBanUser={handleBanUser}
                    onActivateUser={handleActivateUser}
                    onSuspendUser={handleSuspendUser}
                    onDeleteUser={handleDeleteUser}
                    onViewDetails={handleViewUserDetails}
                    onUpdateRole={handleUpdateRole}
                  />
                  {renderPagination(usersPagination, (p) =>
                    setUsersPagination(p)
                  )}
                </>
              ) : (
                <>
                  {filteredUsers.length === 0 ? (
                    <Text
                      style={[styles.emptyText, { color: colors.secondary }]}
                    >
                      {usersSearch
                        ? 'No users found matching your search'
                        : 'No users found'}
                    </Text>
                  ) : (
                    filteredUsers.map((u) => (
                      <View
                        key={u.id}
                        style={[
                          styles.item,
                          {
                            backgroundColor: colors.card,
                            borderColor: colors.border,
                          },
                        ]}
                      >
                        <View style={styles.itemContent}>
                          <Text
                            style={[styles.itemTitle, { color: colors.text }]}
                          >
                            {u.email}
                          </Text>
                          <Text
                            style={[
                              styles.itemSubtitle,
                              { color: colors.secondary },
                            ]}
                          >
                            Created: {new Date(u.created_at).toLocaleString()}
                          </Text>
                          <Text
                            style={[
                              styles.itemSubtitle,
                              { color: colors.secondary },
                            ]}
                          >
                            Subscription: {u.subscription_plan} (
                            {u.subscription_status})
                          </Text>
                        </View>
                        <View style={styles.itemActions}>
                          {u.is_active ? (
                            <TouchableOpacity
                              onPress={() => handleBanUser(u.id)}
                            >
                              <Ionicons
                                name="ban"
                                size={24}
                                color={
                                  colors.errorText || palette.brightMatteRed
                                }
                              />
                            </TouchableOpacity>
                          ) : (
                            <TouchableOpacity
                              onPress={() => handleActivateUser(u.id)}
                            >
                              <Ionicons
                                name="checkmark-circle"
                                size={24}
                                color={colors.success}
                              />
                            </TouchableOpacity>
                          )}
                        </View>
                      </View>
                    ))
                  )}
                  {renderPagination(usersPagination, (p) =>
                    setUsersPagination(p)
                  )}
                </>
              )}
            </View>
          )}

          {activeView === 'subscriptions' && (
            <View style={styles.section}>
              <SearchBar
                value={subscriptionsSearch}
                onChangeText={setSubscriptionsSearch}
                placeholder="Search subscriptions by email, plan, status..."
                containerStyle={styles.searchBarContainer}
              />
              {Platform.OS === 'web' ? (
                <>
                  <SubscriptionsGrid
                    ref={subscriptionsGridRef}
                    subscriptions={subscriptions}
                    searchValue={subscriptionsSearch}
                  />
                  {renderPagination(subscriptionsPagination, (p) =>
                    setSubscriptionsPagination(p)
                  )}
                </>
              ) : (
                <>
                  {filteredSubscriptions.length === 0 ? (
                    <Text
                      style={[styles.emptyText, { color: colors.secondary }]}
                    >
                      {subscriptionsSearch
                        ? 'No subscriptions found matching your search'
                        : 'No subscriptions found'}
                    </Text>
                  ) : (
                    filteredSubscriptions.map((u) => (
                      <View
                        key={u.id}
                        style={[
                          styles.item,
                          {
                            backgroundColor: colors.card,
                            borderColor: colors.border,
                          },
                        ]}
                      >
                        <View style={styles.itemContent}>
                          <Text
                            style={[styles.itemTitle, { color: colors.text }]}
                          >
                            {u.email}
                          </Text>
                          <Text
                            style={[
                              styles.itemSubtitle,
                              { color: colors.secondary },
                            ]}
                          >
                            Plan: {u.subscription_plan} | Status:{' '}
                            {u.subscription_status}
                          </Text>
                          <Text
                            style={[
                              styles.itemSubtitle,
                              { color: colors.secondary },
                            ]}
                          >
                            Created: {new Date(u.created_at).toLocaleString()}
                          </Text>
                        </View>
                      </View>
                    ))
                  )}
                  {renderPagination(subscriptionsPagination, (p) =>
                    setSubscriptionsPagination(p)
                  )}
                </>
              )}
            </View>
          )}

          {activeView === 'moodboards' && (
            <View style={styles.section}>
              <SearchBar
                value={moodboardsSearch}
                onChangeText={setMoodboardsSearch}
                placeholder="Search moodboards by name..."
                containerStyle={styles.searchBarContainer}
              />
              {Platform.OS === 'web' && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: '16px',
                    paddingBottom: '12px',
                    marginBottom: '16px',
                    borderBottom: `1px solid ${colors.border}`,
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={[styles.filterLabel, { color: colors.text }]}>
                      Status:
                    </Text>
                    <select
                      value={moodboardStatusFilter}
                      onChange={(e) => setMoodboardStatusFilter(e.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: `1px solid ${colors.border}`,
                        backgroundColor: colors.card,
                        color: colors.text,
                        fontSize: '12px',
                        marginLeft: '8px',
                      }}
                    >
                      <option value="">All</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="flagged">Flagged</option>
                    </select>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={[styles.filterLabel, { color: colors.text }]}>
                      User ID:
                    </Text>
                    <input
                      type="number"
                      value={moodboardUserFilter}
                      onChange={(e) => setMoodboardUserFilter(e.target.value)}
                      placeholder="Filter by user ID"
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: `1px solid ${colors.border}`,
                        backgroundColor: colors.card,
                        color: colors.text,
                        fontSize: '12px',
                        marginLeft: '8px',
                        width: '120px',
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={[styles.filterLabel, { color: colors.text }]}>
                      Sort:
                    </Text>
                    <select
                      value={moodboardSortBy}
                      onChange={(e) => setMoodboardSortBy(e.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: `1px solid ${colors.border}`,
                        backgroundColor: colors.card,
                        color: colors.text,
                        fontSize: '12px',
                        marginLeft: '8px',
                      }}
                    >
                      <option value="created_at">Date</option>
                      <option value="popularity">Popularity</option>
                      <option value="likes_count">Likes</option>
                      <option value="views_count">Views</option>
                    </select>
                    <select
                      value={moodboardSortOrder}
                      onChange={(e) => setMoodboardSortOrder(e.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: `1px solid ${colors.border}`,
                        backgroundColor: colors.card,
                        color: colors.text,
                        fontSize: '12px',
                        marginLeft: '8px',
                      }}
                    >
                      <option value="desc">Desc</option>
                      <option value="asc">Asc</option>
                    </select>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={[styles.filterLabel, { color: colors.text }]}>
                      From:
                    </Text>
                    <input
                      type="date"
                      value={moodboardDateFrom}
                      onChange={(e) => setMoodboardDateFrom(e.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: `1px solid ${colors.border}`,
                        backgroundColor: colors.card,
                        color: colors.text,
                        fontSize: '12px',
                        marginLeft: '8px',
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={[styles.filterLabel, { color: colors.text }]}>
                      To:
                    </Text>
                    <input
                      type="date"
                      value={moodboardDateTo}
                      onChange={(e) => setMoodboardDateTo(e.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: `1px solid ${colors.border}`,
                        backgroundColor: colors.card,
                        color: colors.text,
                        fontSize: '12px',
                        marginLeft: '8px',
                      }}
                    />
                  </div>
                </div>
              )}
              {Platform.OS === 'web' ? (
                <>
                  <MoodboardsGrid
                    ref={moodboardsGridRef}
                    moodboards={moodboards}
                    searchValue={moodboardsSearch}
                    onHideMoodboard={handleHideMoodboard}
                    onShowMoodboard={handleShowMoodboard}
                    onRejectMoodboard={handleRejectMoodboard}
                    onDeleteMoodboard={handleDeleteMoodboard}
                    onFlagMoodboard={handleFlagMoodboard}
                  />
                  {renderPagination(moodboardsPagination, (p) =>
                    setMoodboardsPagination(p)
                  )}
                </>
              ) : (
                <>
                  {filteredMoodboards.length === 0 ? (
                    <Text
                      style={[styles.emptyText, { color: colors.secondary }]}
                    >
                      {moodboardsSearch
                        ? 'No moodboards found matching your search'
                        : 'No moodboards found'}
                    </Text>
                  ) : (
                    filteredMoodboards.map((mb) => (
                      <View
                        key={mb.id}
                        style={[
                          styles.item,
                          {
                            backgroundColor: colors.card,
                            borderColor: colors.border,
                          },
                        ]}
                      >
                        <View style={styles.itemContent}>
                          <Text
                            style={[styles.itemTitle, { color: colors.text }]}
                          >
                            {mb.name}
                          </Text>
                          <Text
                            style={[
                              styles.itemSubtitle,
                              { color: colors.secondary },
                            ]}
                          >
                            User ID: {mb.user_id} | Public:{' '}
                            {mb.is_public ? 'Yes' : 'No'}
                          </Text>
                          <Text
                            style={[
                              styles.itemSubtitle,
                              { color: colors.secondary },
                            ]}
                          >
                            Likes: {mb.likes_count} | Views: {mb.views_count}
                          </Text>
                        </View>
                        <TouchableOpacity
                          onPress={() => handleHideMoodboard(mb.id)}
                        >
                          <Ionicons
                            name="eye-off"
                            size={24}
                            color={colors.secondary}
                          />
                        </TouchableOpacity>
                      </View>
                    ))
                  )}
                  {renderPagination(moodboardsPagination, (p) =>
                    setMoodboardsPagination(p)
                  )}
                </>
              )}
            </View>
          )}

          {activeView === 'flagged' && (
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>
                Flagged Content Moderation
              </Text>
              <SearchBar
                value={flaggedSearch}
                onChangeText={setFlaggedSearch}
                placeholder="Search flagged moodboards by name, user, email..."
                containerStyle={styles.searchBarContainer}
              />
              {Platform.OS === 'web' ? (
                <>
                  <MoodboardsGrid
                    ref={moodboardsGridRef}
                    moodboards={flaggedMoodboards}
                    searchValue={flaggedSearch}
                    onRejectMoodboard={(id) => {
                      const mb = flaggedMoodboards.find((m) => m.id === id);
                      handleRejectFlaggedMoodboard(id, mb?.name);
                    }}
                    onApproveMoodboard={(id) => {
                      const mb = flaggedMoodboards.find((m) => m.id === id);
                      handleApproveFlaggedMoodboard(id, mb?.name);
                    }}
                    onDeleteMoodboard={(id) => {
                      const mb = flaggedMoodboards.find((m) => m.id === id);
                      handleDeleteFlaggedMoodboard(id, mb?.name);
                    }}
                    onFlagMoodboard={handleFlagMoodboard}
                  />
                  {renderPagination(flaggedPagination, (p) =>
                    setFlaggedPagination(p)
                  )}
                </>
              ) : (
                <>
                  {filteredFlaggedMoodboards.length === 0 ? (
                    <Text
                      style={[styles.emptyText, { color: colors.secondary }]}
                    >
                      {flaggedSearch
                        ? 'No flagged moodboards found matching your search'
                        : 'No flagged moodboards found'}
                    </Text>
                  ) : (
                    filteredFlaggedMoodboards.map((mb) => (
                      <View
                        key={mb.id}
                        style={[
                          styles.item,
                          {
                            backgroundColor: colors.card,
                            borderColor: colors.border,
                          },
                        ]}
                      >
                        <View style={styles.itemContent}>
                          <Text
                            style={[styles.itemTitle, { color: colors.text }]}
                          >
                            {mb.name}
                          </Text>
                          <Text
                            style={[
                              styles.itemSubtitle,
                              { color: colors.secondary },
                            ]}
                          >
                            Reports: {mb.reported_count || 0} | User:{' '}
                            {mb.user_name ||
                              mb.user_email ||
                              `ID: ${mb.user_id}`}
                          </Text>
                        </View>
                        <View style={{ flexDirection: 'row', gap: 8 }}>
                          <TouchableOpacity
                            onPress={() =>
                              handleRejectFlaggedMoodboard(mb.id, mb.name)
                            }
                            style={{
                              padding: 8,
                              backgroundColor: palette.brightMatteRed,
                              borderRadius: 4,
                            }}
                          >
                            <Text
                              style={{ color: palette.white, fontSize: 12 }}
                            >
                              Reject
                            </Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            onPress={() =>
                              handleApproveFlaggedMoodboard(mb.id, mb.name)
                            }
                            style={{
                              padding: 8,
                              backgroundColor: palette.cyanLimeGreen,
                              borderRadius: 4,
                            }}
                          >
                            <Text
                              style={{ color: palette.white, fontSize: 12 }}
                            >
                              Approve
                            </Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            onPress={() =>
                              handleDeleteFlaggedMoodboard(mb.id, mb.name)
                            }
                            style={{
                              padding: 8,
                              backgroundColor: palette.brightRed,
                              borderRadius: 4,
                            }}
                          >
                            <Text
                              style={{ color: palette.white, fontSize: 12 }}
                            >
                              Delete
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    ))
                  )}
                  {renderPagination(flaggedPagination, (p) =>
                    setFlaggedPagination(p)
                  )}
                </>
              )}
            </View>
          )}

          {activeView === 'feedback' && (
            <View style={styles.section}>
              <SearchBar
                value={feedbackSearch}
                onChangeText={setFeedbackSearch}
                placeholder="Search feedback by subject, message, email..."
                containerStyle={styles.searchBarContainer}
              />
              {Platform.OS === 'web' ? (
                <>
                  <FeedbackGrid
                    ref={feedbackGridRef}
                    feedback={feedback}
                    searchValue={feedbackSearch}
                    onUpdateStatus={(feedbackId, status) => {
                      updateFeedbackStatus.mutate({ feedbackId, status });
                    }}
                  />
                  {renderPagination(feedbackPagination, (p) =>
                    setFeedbackPagination(p)
                  )}
                </>
              ) : (
                <>
                  {filteredFeedback.length === 0 ? (
                    <Text
                      style={[styles.emptyText, { color: colors.secondary }]}
                    >
                      {feedbackSearch
                        ? 'No feedback found matching your search'
                        : 'No feedback found'}
                    </Text>
                  ) : (
                    filteredFeedback.map((fb) => {
                      const currentStatus = fb.status || 'new';
                      const statusOptions = [
                        {
                          value: 'reviewing',
                          label: 'Reviewing',
                          color: palette.brightBlue,
                        },
                        {
                          value: 'in_progress',
                          label: 'In Progress',
                          color: palette.lightOrange,
                        },
                        {
                          value: 'done',
                          label: 'Done',
                          color: palette.cyanLimeGreen,
                        },
                        {
                          value: 'no_action_needed',
                          label: 'No Action',
                          color: palette.darkGrayBlue,
                        },
                      ];

                      return (
                        <View
                          key={fb.id}
                          style={[
                            styles.item,
                            {
                              backgroundColor: colors.card,
                              borderColor: colors.border,
                            },
                          ]}
                        >
                          <Text
                            style={[styles.itemTitle, { color: colors.text }]}
                          >
                            {fb.subject || 'No Subject'}
                          </Text>
                          <Text
                            style={[
                              styles.itemSubtitle,
                              { color: colors.secondary },
                            ]}
                          >
                            {fb.message}
                          </Text>
                          <View style={styles.feedbackMetaRow}>
                            <Text
                              style={[
                                styles.itemSubtitle,
                                { color: colors.secondary },
                              ]}
                            >
                              From: {fb.email || 'Anonymous'} | Category:{' '}
                              {fb.category}
                            </Text>
                            <View
                              style={[
                                styles.statusBadge,
                                {
                                  backgroundColor: getStatusColor(
                                    fb.status || 'new'
                                  ).background,
                                  borderColor: getStatusColor(
                                    fb.status || 'new'
                                  ).color,
                                },
                              ]}
                            >
                              <Text
                                style={[
                                  styles.statusBadgeText,
                                  {
                                    color: getStatusColor(fb.status || 'new')
                                      .color,
                                  },
                                ]}
                              >
                                {getStatusLabel(fb.status || 'new')}
                              </Text>
                            </View>
                          </View>
                          <View style={styles.feedbackDatesRow}>
                            <Text
                              style={[
                                styles.itemSubtitle,
                                { color: colors.secondary, fontSize: 10 },
                              ]}
                            >
                              Created:{' '}
                              {new Date(fb.created_at).toLocaleString()}
                            </Text>
                            <Text
                              style={[
                                styles.itemSubtitle,
                                { color: colors.secondary, fontSize: 10 },
                              ]}
                            >
                              Updated:{' '}
                              {new Date(
                                fb.updated_at || fb.created_at
                              ).toLocaleString()}
                            </Text>
                            {fb.completed_at && (
                              <Text
                                style={[
                                  styles.itemSubtitle,
                                  { color: colors.secondary, fontSize: 10 },
                                ]}
                              >
                                Completed:{' '}
                                {new Date(fb.completed_at).toLocaleString()}
                              </Text>
                            )}
                          </View>
                          <View style={styles.statusActionsContainer}>
                            {statusOptions.map((option) => {
                              const isActive = currentStatus === option.value;
                              return (
                                <TouchableOpacity
                                  key={option.value}
                                  onPress={() =>
                                    updateFeedbackStatus.mutate({
                                      feedbackId: fb.id,
                                      status: option.value,
                                    })
                                  }
                                  style={[
                                    styles.statusButton,
                                    {
                                      backgroundColor: isActive
                                        ? option.color
                                        : `${option.color}20`,
                                      borderColor: option.color,
                                    },
                                  ]}
                                >
                                  <Text
                                    style={[
                                      styles.statusButtonText,
                                      {
                                        color: isActive
                                          ? 'white'
                                          : option.color,
                                      },
                                    ]}
                                  >
                                    {option.label}
                                  </Text>
                                </TouchableOpacity>
                              );
                            })}
                          </View>
                        </View>
                      );
                    })
                  )}
                  {renderPagination(feedbackPagination, (p) =>
                    setFeedbackPagination(p)
                  )}
                </>
              )}
            </View>
          )}

          {activeView === 'audit-logs' && (
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>
                Audit Log
              </Text>
              <SearchBar
                value={auditLogsSearch}
                onChangeText={setAuditLogsSearch}
                placeholder="Search audit logs by action, target, admin, reason..."
                containerStyle={styles.searchBarContainer}
              />
              {Platform.OS === 'web' ? (
                <>
                  <AuditLogsGrid
                    ref={auditLogsGridRef}
                    auditLogs={auditLogs}
                    searchValue={auditLogsSearch}
                  />
                  {renderPagination(auditLogsPagination, (p) =>
                    setAuditLogsPagination(p)
                  )}
                </>
              ) : (
                <>
                  {filteredAuditLogs.length === 0 ? (
                    <Text
                      style={[styles.emptyText, { color: colors.secondary }]}
                    >
                      {auditLogsSearch
                        ? 'No audit logs found matching your search'
                        : 'No audit logs found'}
                    </Text>
                  ) : (
                    filteredAuditLogs.map(
                      (log: Record<string, unknown> & { id?: unknown }) => (
                        <View
                          key={log.id}
                          style={[
                            styles.item,
                            {
                              backgroundColor: colors.card,
                              borderColor: colors.border,
                            },
                          ]}
                        >
                          <View style={styles.itemContent}>
                            <Text
                              style={[styles.itemTitle, { color: colors.text }]}
                            >
                              {log.action} - {log.target_type}
                            </Text>
                            <Text
                              style={[
                                styles.itemSubtitle,
                                { color: colors.secondary },
                              ]}
                            >
                              Admin:{' '}
                              {log.admin_user_email ||
                                `ID: ${log.admin_user_id}` ||
                                'Unknown'}
                            </Text>
                            <Text
                              style={[
                                styles.itemSubtitle,
                                { color: colors.secondary },
                              ]}
                            >
                              Target:{' '}
                              {log.target_name || `ID: ${log.target_id}`}
                            </Text>
                            {log.reason && (
                              <Text
                                style={[
                                  styles.itemSubtitle,
                                  { color: colors.secondary },
                                ]}
                              >
                                Reason: {log.reason}
                              </Text>
                            )}
                            <Text
                              style={[
                                styles.itemSubtitle,
                                { color: colors.secondary, fontSize: 11 },
                              ]}
                            >
                              {new Date(log.created_at).toLocaleString()}
                            </Text>
                            {log.confirmed && (
                              <Text
                                style={[
                                  styles.itemSubtitle,
                                  {
                                    color: palette.cyanLimeGreen,
                                    fontSize: 11,
                                  },
                                ]}
                              >
                                ✓ Confirmed
                              </Text>
                            )}
                          </View>
                        </View>
                      )
                    )
                  )}
                  {renderPagination(auditLogsPagination, (p) =>
                    setAuditLogsPagination(p)
                  )}
                </>
              )}
            </View>
          )}

          {activeView === 'analytics' && <AnalyticsView />}

          {activeView === 'support' && <SupportView />}

          {activeView === 'permissions' && <PermissionsView />}

          {activeView === 'feature-flags' && (
            <View style={styles.section}>
              <SearchBar
                value={featureFlagsSearch}
                onChangeText={setFeatureFlagsSearch}
                placeholder="Search feature flags by name, description..."
                containerStyle={styles.searchBarContainer}
              />
              <FeatureFlagsGrid
                searchValue={featureFlagsSearch}
                currentPage={featureFlagsPagination.page}
                pageSize={featureFlagsPagination.limit}
                onPageChange={(page) =>
                  setFeatureFlagsPagination((prev) => ({ ...prev, page }))
                }
              />
              {renderPagination(featureFlagsPagination, (p) =>
                setFeatureFlagsPagination(p)
              )}
            </View>
          )}

          {activeView === 'invites' && (
            <View style={styles.section}>
              <SearchBar
                value={invitesSearch}
                onChangeText={setInvitesSearch}
                placeholder="Search invites by code, inviter, invitee..."
                containerStyle={styles.searchBarContainer}
              />
              {Platform.OS === 'web' ? (
                <>
                  <InvitesGrid
                    ref={invitesGridRef}
                    invites={invitesData?.items || []}
                    searchValue={invitesSearch}
                  />
                  {renderPagination(invitesPagination, (p) =>
                    setInvitesPagination(p)
                  )}
                </>
              ) : (
                <>
                  {filteredInvites.length === 0 ? (
                    <Text
                      style={[styles.emptyText, { color: colors.secondary }]}
                    >
                      {invitesSearch
                        ? 'No invites found matching your search'
                        : 'No invites found'}
                    </Text>
                  ) : (
                    filteredInvites.map((invite) => (
                      <View
                        key={invite.id}
                        style={[
                          styles.item,
                          {
                            backgroundColor: colors.card,
                            borderColor: colors.border,
                          },
                        ]}
                      >
                        <View style={styles.itemContent}>
                          <Text
                            style={[styles.itemTitle, { color: colors.text }]}
                          >
                            Code: {invite.code}
                          </Text>
                          <Text
                            style={[
                              styles.itemSubtitle,
                              { color: colors.secondary },
                            ]}
                          >
                            Inviter:{' '}
                            {invite.inviter_name || invite.inviter_email} (ID:{' '}
                            {invite.inviter_id})
                          </Text>
                          {invite.invitee_id ? (
                            <Text
                              style={[
                                styles.itemSubtitle,
                                { color: colors.secondary },
                              ]}
                            >
                              Invitee:{' '}
                              {invite.invitee_name || invite.invitee_email} (ID:{' '}
                              {invite.invitee_id})
                            </Text>
                          ) : (
                            <Text
                              style={[
                                styles.itemSubtitle,
                                { color: colors.secondary },
                              ]}
                            >
                              Status: Not used
                            </Text>
                          )}
                          <Text
                            style={[
                              styles.itemSubtitle,
                              { color: colors.secondary, fontSize: 11 },
                            ]}
                          >
                            Created:{' '}
                            {new Date(invite.created_at).toLocaleString()}
                          </Text>
                          {invite.used_at && (
                            <Text
                              style={[
                                styles.itemSubtitle,
                                { color: colors.secondary, fontSize: 11 },
                              ]}
                            >
                              Used: {new Date(invite.used_at).toLocaleString()}
                            </Text>
                          )}
                        </View>
                      </View>
                    ))
                  )}
                  {renderPagination(invitesPagination, (p) =>
                    setInvitesPagination(p)
                  )}
                </>
              )}
            </View>
          )}

          {activeView === 'profile' && user && (
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>
                My Profile
              </Text>

              {adminUserDetailsLoading ? (
                <View
                  style={[
                    styles.loadingContainer,
                    { backgroundColor: colors.background },
                  ]}
                >
                  <Text style={[styles.loading, { color: colors.secondary }]}>
                    Loading profile...
                  </Text>
                </View>
              ) : adminUserDetails ? (
                <ScrollView>
                  {/* Profile Header */}
                  <View
                    style={[
                      styles.profileHeader,
                      {
                        backgroundColor: colors.card,
                        borderColor: colors.border,
                      },
                    ]}
                  >
                    <View style={styles.profileHeaderContent}>
                      <UserAvatar
                        size={80}
                        imageUrl={adminUserDetails.profile_image_url}
                        email={adminUserDetails.email}
                      />
                      <View style={styles.profileHeaderInfo}>
                        <Text
                          style={[styles.profileName, { color: colors.text }]}
                        >
                          {adminUserDetails.name ||
                            adminUserDetails.email?.split('@')[0] ||
                            'Admin'}
                        </Text>
                        <Text
                          style={[
                            styles.profileEmail,
                            { color: colors.secondary },
                          ]}
                        >
                          {adminUserDetails.email}
                        </Text>
                        <View style={styles.profileBadges}>
                          {adminUserDetails.role &&
                            adminUserDetails.role !== 'user' && (
                              <View
                                style={[
                                  styles.badge,
                                  {
                                    backgroundColor: `${palette.brightGreen}20`,
                                    borderColor: palette.brightGreen,
                                  },
                                ]}
                              >
                                <Text
                                  style={[
                                    styles.badgeText,
                                    { color: palette.brightGreen },
                                  ]}
                                >
                                  {adminUserDetails.role
                                    .charAt(0)
                                    .toUpperCase() +
                                    adminUserDetails.role.slice(1)}
                                </Text>
                              </View>
                            )}
                        </View>
                      </View>
                    </View>
                  </View>

                  {/* Account Information */}
                  <View
                    style={[
                      styles.profileSection,
                      {
                        backgroundColor: colors.card,
                        borderColor: colors.border,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.sectionTitle,
                        { color: colors.text, marginBottom: 16 },
                      ]}
                    >
                      Account Information
                    </Text>

                    <View style={styles.infoRow}>
                      <Text
                        style={[styles.infoLabel, { color: colors.secondary }]}
                      >
                        User ID:
                      </Text>
                      <Text style={[styles.infoValue, { color: colors.text }]}>
                        {adminUserDetails.id}
                      </Text>
                    </View>

                    <View style={styles.infoRow}>
                      <Text
                        style={[styles.infoLabel, { color: colors.secondary }]}
                      >
                        Email:
                      </Text>
                      <Text style={[styles.infoValue, { color: colors.text }]}>
                        {adminUserDetails.email}
                      </Text>
                    </View>

                    {adminUserDetails.name && (
                      <View style={styles.infoRow}>
                        <Text
                          style={[
                            styles.infoLabel,
                            { color: colors.secondary },
                          ]}
                        >
                          Name:
                        </Text>
                        <Text
                          style={[styles.infoValue, { color: colors.text }]}
                        >
                          {adminUserDetails.name}
                        </Text>
                      </View>
                    )}

                    <View style={styles.infoRow}>
                      <Text
                        style={[styles.infoLabel, { color: colors.secondary }]}
                      >
                        Role:
                      </Text>
                      <Text style={[styles.infoValue, { color: colors.text }]}>
                        {adminUserDetails.role
                          ? adminUserDetails.role.charAt(0).toUpperCase() +
                            adminUserDetails.role.slice(1)
                          : 'User'}
                      </Text>
                    </View>

                    <View style={styles.infoRow}>
                      <Text
                        style={[styles.infoLabel, { color: colors.secondary }]}
                      >
                        Account Status:
                      </Text>
                      <Text
                        style={[
                          styles.infoValue,
                          {
                            color: adminUserDetails.is_active
                              ? palette.cyanLimeGreen
                              : palette.brightMatteRed,
                          },
                        ]}
                      >
                        {adminUserDetails.account_status
                          ? adminUserDetails.account_status
                              .charAt(0)
                              .toUpperCase() +
                            adminUserDetails.account_status.slice(1)
                          : adminUserDetails.is_active
                            ? 'Active'
                            : 'Inactive'}
                      </Text>
                    </View>

                    {adminUserDetails.age_verified !== undefined && (
                      <View style={styles.infoRow}>
                        <Text
                          style={[
                            styles.infoLabel,
                            { color: colors.secondary },
                          ]}
                        >
                          Age Verified:
                        </Text>
                        <Text
                          style={[
                            styles.infoValue,
                            {
                              color: adminUserDetails.age_verified
                                ? palette.cyanLimeGreen
                                : colors.secondary,
                            },
                          ]}
                        >
                          {adminUserDetails.age_verified ? 'Yes' : 'No'}
                        </Text>
                      </View>
                    )}
                  </View>

                  {/* Subscription Information */}
                  <View
                    style={[
                      styles.profileSection,
                      {
                        backgroundColor: colors.card,
                        borderColor: colors.border,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.sectionTitle,
                        { color: colors.text, marginBottom: 16 },
                      ]}
                    >
                      Subscription
                    </Text>

                    <View style={styles.infoRow}>
                      <Text
                        style={[styles.infoLabel, { color: colors.secondary }]}
                      >
                        Plan:
                      </Text>
                      <Text style={[styles.infoValue, { color: colors.text }]}>
                        {adminUserDetails.subscription_plan
                          ? adminUserDetails.subscription_plan
                              .charAt(0)
                              .toUpperCase() +
                            adminUserDetails.subscription_plan.slice(1)
                          : 'Free'}
                      </Text>
                    </View>

                    <View style={styles.infoRow}>
                      <Text
                        style={[styles.infoLabel, { color: colors.secondary }]}
                      >
                        Status:
                      </Text>
                      <Text style={[styles.infoValue, { color: colors.text }]}>
                        {adminUserDetails.subscription_status
                          ? adminUserDetails.subscription_status
                              .charAt(0)
                              .toUpperCase() +
                            adminUserDetails.subscription_status.slice(1)
                          : 'Active'}
                      </Text>
                    </View>

                    {adminUserDetails.subscription_expires_at && (
                      <View style={styles.infoRow}>
                        <Text
                          style={[
                            styles.infoLabel,
                            { color: colors.secondary },
                          ]}
                        >
                          Expires At:
                        </Text>
                        <Text
                          style={[styles.infoValue, { color: colors.text }]}
                        >
                          {new Date(
                            adminUserDetails.subscription_expires_at
                          ).toLocaleString()}
                        </Text>
                      </View>
                    )}
                  </View>

                  {/* Test subscription (no payment) */}
                  <View
                    style={[
                      styles.profileSection,
                      {
                        backgroundColor: colors.card,
                        borderColor: colors.border,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.sectionTitle,
                        { color: colors.text, marginBottom: 8 },
                      ]}
                    >
                      Test subscription
                    </Text>
                    <Text
                      style={[
                        styles.infoLabel,
                        { color: colors.secondary, marginBottom: 12 },
                      ]}
                    >
                      Change your plan for testing (no Paddle payment). App will
                      reflect the new plan after refresh.
                    </Text>
                    <View style={styles.testSubscriptionRow}>
                      {[
                        { plan: 'free', label: 'Free' },
                        { plan: 'plus', label: 'Plus' },
                        { plan: 'pro', label: 'Pro' },
                        { plan: 'freemium_trial', label: 'Trial (7d)' },
                        { plan: 'pay_per_scan', label: 'Pay-per-scan' },
                      ].map(({ plan, label }) => {
                        const isCurrent =
                          adminUserDetails.subscription_plan === plan;
                        return (
                          <TouchableOpacity
                            key={plan}
                            onPress={async () => {
                              const userId =
                                typeof adminUserDetails.id === 'number'
                                  ? adminUserDetails.id
                                  : parseInt(String(adminUserDetails.id), 10);
                              try {
                                await updateSubscriptionMutation.mutateAsync({
                                  userId,
                                  subscription_plan: plan,
                                });
                                refreshUser?.();
                              } catch {
                                // Error already shown by mutation
                              }
                            }}
                            disabled={updateSubscriptionMutation.isPending}
                            style={[
                              styles.testSubscriptionButton,
                              {
                                backgroundColor: isCurrent
                                  ? palette.softViolet
                                  : colors.border,
                                opacity: updateSubscriptionMutation.isPending
                                  ? 0.6
                                  : 1,
                              },
                            ]}
                          >
                            <Text
                              style={[
                                styles.testSubscriptionButtonText,
                                {
                                  color: isCurrent ? '#fff' : colors.text,
                                },
                              ]}
                            >
                              {label}
                            </Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  </View>

                  {/* Dates */}
                  <View
                    style={[
                      styles.profileSection,
                      {
                        backgroundColor: colors.card,
                        borderColor: colors.border,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.sectionTitle,
                        { color: colors.text, marginBottom: 16 },
                      ]}
                    >
                      Dates
                    </Text>

                    <View style={styles.infoRow}>
                      <Text
                        style={[styles.infoLabel, { color: colors.secondary }]}
                      >
                        Created:
                      </Text>
                      <Text style={[styles.infoValue, { color: colors.text }]}>
                        {new Date(adminUserDetails.created_at).toLocaleString()}
                      </Text>
                    </View>

                    <View style={styles.infoRow}>
                      <Text
                        style={[styles.infoLabel, { color: colors.secondary }]}
                      >
                        Last Updated:
                      </Text>
                      <Text style={[styles.infoValue, { color: colors.text }]}>
                        {new Date(adminUserDetails.updated_at).toLocaleString()}
                      </Text>
                    </View>

                    {adminUserDetails.last_login && (
                      <View style={styles.infoRow}>
                        <Text
                          style={[
                            styles.infoLabel,
                            { color: colors.secondary },
                          ]}
                        >
                          Last Login:
                        </Text>
                        <Text
                          style={[styles.infoValue, { color: colors.text }]}
                        >
                          {new Date(
                            adminUserDetails.last_login
                          ).toLocaleString()}
                        </Text>
                      </View>
                    )}
                  </View>

                  {/* Statistics */}
                  {adminUserDetails.stats && (
                    <View
                      style={[
                        styles.profileSection,
                        {
                          backgroundColor: colors.card,
                          borderColor: colors.border,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.sectionTitle,
                          { color: colors.text, marginBottom: 16 },
                        ]}
                      >
                        Statistics
                      </Text>

                      <View style={styles.infoRow}>
                        <Text
                          style={[
                            styles.infoLabel,
                            { color: colors.secondary },
                          ]}
                        >
                          Colour Profiles:
                        </Text>
                        <Text
                          style={[styles.infoValue, { color: colors.text }]}
                        >
                          {adminUserDetails.stats.colour_profiles_count || 0}
                        </Text>
                      </View>

                      <View style={styles.infoRow}>
                        <Text
                          style={[
                            styles.infoLabel,
                            { color: colors.secondary },
                          ]}
                        >
                          Moodboards:
                        </Text>
                        <Text
                          style={[styles.infoValue, { color: colors.text }]}
                        >
                          {adminUserDetails.stats.moodboards_count || 0}
                        </Text>
                      </View>

                      <View style={styles.infoRow}>
                        <Text
                          style={[
                            styles.infoLabel,
                            { color: colors.secondary },
                          ]}
                        >
                          Total Activity:
                        </Text>
                        <Text
                          style={[styles.infoValue, { color: colors.text }]}
                        >
                          {adminUserDetails.stats.total_activity || 0}
                        </Text>
                      </View>
                    </View>
                  )}
                </ScrollView>
              ) : (
                <View style={styles.emptyTextContainer}>
                  <Text style={[styles.emptyText, { color: colors.secondary }]}>
                    Failed to load profile information
                  </Text>
                </View>
              )}
            </View>
          )}
        </DashboardLayout>
      </View>
      <UserDetailModal
        visible={userDetailModalVisible}
        userId={selectedUserId}
        onClose={() => {
          setUserDetailModalVisible(false);
          setSelectedUserId(null);
        }}
        onRefresh={() => {
          // TanStack Query will automatically refetch
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...(Platform.OS === 'web'
      ? {
          paddingTop: 60, // Space for TopNavigationBar
        }
      : {
          paddingTop: 60, // Space for TopNavigationBar on mobile too
        }),
  },
  contentContainer: {
    flex: 1,
    ...(Platform.OS === 'web'
      ? {
          marginTop: 0, // TopNavigationBar is absolutely positioned
        }
      : {
          marginTop: 0,
        }),
  },
  layout: {
    flex: 1,
    flexDirection: 'row',
  },
  sidebar: {
    borderRightWidth: 1,
    ...(Platform.OS === 'web'
      ? {
          minWidth: 250,
          maxWidth: 250,
        }
      : {}),
  },
  sidebarHeader: {
    padding: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  sidebarTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  sidebarContent: {
    flex: 1,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    marginHorizontal: 8,
    marginVertical: 4,
    borderRadius: 8,
  },
  menuIcon: {
    marginRight: 12,
  },
  menuText: {
    fontSize: 16,
    fontWeight: '500',
  },
  mainContent: {
    flex: 1,
  },
  menuButton: {
    position: 'absolute',
    top: 10,
    left: 10,
    zIndex: 10,
    padding: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
    maxWidth: 1200,
    alignSelf: 'center',
    width: '100%',
  },
  contentMobile: {
    padding: 16,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 16,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 16,
  },
  statCard: {
    flex: 1,
    minWidth: 200,
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    ...(Platform.OS === 'web'
      ? {
          maxWidth: 280,
        }
      : {}),
  },
  statValue: {
    fontSize: 12,
    fontWeight: '700',
    marginTop: 8,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 8,
  },
  statRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  statSubtext: {
    fontSize: 12,
  },
  item: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  itemContent: {
    flex: 1,
  },
  itemTitle: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 4,
  },
  itemSubtitle: {
    fontSize: 12,
    marginBottom: 2,
  },
  itemActions: {
    marginLeft: 16,
  },
  pagination: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    marginVertical: 16,
  },
  paginationButton: {
    padding: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  paginationText: {
    fontSize: 12,
  },
  loading: {
    textAlign: 'center',
    marginTop: 40,
    fontSize: 12,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  emptyText: {
    textAlign: 'center',
    fontSize: 12,
  },
  emptyTextContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchBarContainer: {
    marginBottom: 16,
  },
  filterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    paddingBottom: 12,
    marginBottom: 16,
    borderBottomWidth: 1,
    alignItems: 'center',
  },
  filterGroup: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  listContainer: {
    maxHeight: Platform.OS === 'web' ? 400 : undefined,
    ...(Platform.OS === 'web'
      ? {
          overflow: 'hidden',
          // Hide scrollbars
          ...(Platform.OS === 'web'
            ? {
                // @ts-ignore - web-only CSS
                scrollbarWidth: 'none',
                // @ts-ignore - web-only CSS
                msOverflowStyle: 'none',
              }
            : {}),
        }
      : {}),
  },
  statusActionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
  },
  statusButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    minWidth: 80,
    alignItems: 'center',
  },
  statusButtonText: {
    fontSize: 11,
    fontWeight: '600',
  },
  feedbackMetaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
    flexWrap: 'wrap',
    gap: 8,
  },
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    borderWidth: 1,
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: '600',
  },
  feedbackDatesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 8,
  },
  profileHeader: {
    padding: 24,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 16,
  },
  profileHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  profileHeaderInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 4,
  },
  profileEmail: {
    fontSize: 14,
    marginBottom: 12,
  },
  profileBadges: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  badge: {
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  profileSection: {
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 16,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.05)',
  },
  infoLabel: {
    fontSize: 14,
    fontWeight: '500',
    flex: 1,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: '400',
    flex: 2,
    textAlign: 'right',
  },
  testSubscriptionRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  testSubscriptionButton: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
  },
  testSubscriptionButtonText: {
    fontSize: 13,
    fontWeight: '600',
  },
});

export default AdminScreen;
